import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import browser from '@openclassrooms/utils/browser';
import { errorTracker } from 'Modules/event';
import linkHeaderParser from 'linkheader-parser/dist/linkheader-parser-node';
import InvalidOptionsException from 'lib/exceptions/InvalidOptionsException';
import XMLHttpRequestException from './XMLHttpRequestException';

export * from './ajaxStates';

export const serialize = (obj) =>
  `${Object.keys(obj)
    .reduce((a, k) => {
      a.push(`${k}=${encodeURIComponent(obj[k])}`);
      return a;
    }, [])
    .join('&')}`;

const getContentType = (headers) => headers.get('content-type');

const getLinks = (headers) => headers.get('link');

export const ajax = (url, defaultOptions = {}) => {
  const options = {
    headerXRequestedWith: true,
    ...defaultOptions,
  };
  let body;
  let queryString = '';

  if (!url) {
    throw new InvalidOptionsException();
  }

  if (!options.type) {
    options.type = 'GET';
  }

  if (!options.headers) {
    options.headers = {};
  }

  if (options.start !== undefined && options.end !== undefined) {
    options.headers.Range = `items=${options.start}-${options.end}`;
  }

  const isGet = options.type === 'GET';

  const { headers: optionsHeaders, type: optionsType, data, formData, ...otherOptions } = options;

  if (options.headerXRequestedWith) {
    optionsHeaders['X-Requested-With'] = 'XMLHttpRequest';
  }

  if (formData) {
    body = new browser.window.FormData();
    Object.keys(formData).forEach((key) => {
      body.append(key, formData[key]);
    });
  } else if (data && isGet) {
    queryString = `${url.indexOf('?') === -1 ? '?' : '&'}${serialize(data)}`;
  } else if (data) {
    body = JSON.stringify(data);
  }

  const request = browser.window.fetch(url + queryString, {
    headers: {
      Accept: 'application/json',
      ...optionsHeaders,
    },
    method: optionsType,
    cache: 'no-cache',
    credentials: 'same-origin',
    ...(!isGet ? { body } : {}),
    ...otherOptions,
  });

  return request
    .then((response) => {
      if (!response.ok && response.type !== 'opaque') {
        throw response;
      }
      return response;
    })
    .then(async (response) => {
      const contentType = getContentType(response.headers);

      let responseJSON;

      try {
        if (contentType.indexOf('text/calendar') !== -1) {
          responseJSON = await response.text();
        } else {
          responseJSON = await response.json();
        }
      } catch (e) {
        responseJSON = '';
      }

      if (isString(response) && !isEmpty(response)) {
        try {
          responseJSON = JSON.parse(response);
        } catch (e) {
          /* ... */
        }
      }

      const linkHeaders = getLinks(response.headers);
      let links;
      if (linkHeaders) {
        links = linkHeaderParser.parse(linkHeaders);
      }

      return {
        headers: response.headers,
        links,
        data: responseJSON,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch(async (error) => {
      let errorObj;

      if (error.status) {
        let responseJSON = null;

        try {
          responseJSON = await error.json();
        } catch (e) {
          try {
            responseJSON = JSON.parse(error.responseText);
          } catch (er) {
            /* */
          }
        }

        errorObj = {
          headers: error.headers || null,
          responseJSON,
          status: error.status || null,
          responseText: error.responseText || null,
          statusText: error.statusText || null,
        };
      } else {
        errorObj = { message: error.message };
      }

      if (options.swallowError) {
        return;
      }

      const errorToThrow = new XMLHttpRequestException(errorObj);

      if (typeof options.noLog === 'function' && options.noLog(errorObj)) {
        throw errorToThrow;
      }

      errorTracker.captureError(errorToThrow, { context: errorObj, type: 'ajax', rethrow: true });
    });
};

const ajaxService = {
  request: ajax,
};

export const ajaxPost = (url, options) => {
  const postConf = {
    type: 'POST',
  };

  return ajaxService.request(url, merge({}, options, postConf));
};

ajaxService.post = ajaxPost;

export const ajaxPut = (url, options) => {
  const putConf = {
    type: 'PUT',
  };

  return ajaxService.request(url, merge({}, options, putConf));
};

ajaxService.put = ajaxPut;

export const ajaxGet = (url, options) => {
  const getConf = {
    type: 'GET',
  };

  return ajaxService.request(url, merge({}, options, getConf));
};

ajaxService.get = ajaxGet;

export default ajaxService;
